import { PayinIcon } from "@images/svgIcons/payinIcon"
import { PayoutIcon } from "@images/svgIcons/payoutIcon"
import { useCurrentBox } from "@modules/box/useBox"
import { useBoxOffers } from "@modules/boxOffer/hooks/useBoxOffer"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { BoxTransactionsDetailsLayout } from "@modules/finances/components/boxTransactionDetailsLayout"
import { BoxTransactionsTable } from "@modules/finances/components/boxTransactionsTable"
import { FinanceCard } from "@modules/finances/components/financeCard"
import { FinanceTotalMonthCard } from "@modules/finances/components/financeTotalMonthCard"
import { LastUpdateComponent } from "@modules/finances/components/lastUpdateInformation"
import { StripeAccountErrorItem } from "@modules/finances/components/stripeAccountErrorAlert"
import { StripeStatusBanner } from "@modules/finances/components/stripeStatusBanner"
import { useFinanceStats } from "@modules/finances/hooks/useFinance"
import { usePeriodParam } from "@modules/finances/hooks/usePeriod"
import { BoxPayoutLayout } from "@modules/financesPayout/components/boxPayoutLayout"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { Path } from "@modules/navigation/routes"
import { useBoxNavigate } from "@modules/navigation/useBoxNavigate"
import { usePromoCodeSidebar } from "@modules/promoCode/hooks/usePromoCodeSidebar"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { ScreenHeader } from "@modules/ui/components/header"
import { useToast } from "@modules/ui/components/huToast"
import { HURow, HUStack } from "@modules/ui/components/layout"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { MenuItem } from "primereact/menuitem"
import { TabMenu } from "primereact/tabmenu"
import { useEffect, useMemo, useState } from "react"
import { useTheme } from "styled-components"

export const BoxFinancesPage: React.FC = () => {
  const language = useLanguage()
  const theme = useTheme()
  const [period] = usePeriodParam()
  const toast = useToast()
  const { result: box, isLoading: isLoadingCurrentBox } = useCurrentBox()
  const boxNavigate = useBoxNavigate()
  const { localStorageService } = useServicesContext()
  const { data: offers } = useBoxOffers({ onlyWithPrice: true })
  const { sidebarNavigateTo } = useSidebar()
  const { navigateToPromoCodeListView } = usePromoCodeSidebar()
  const canSeeBankDetails = useBoxStaffPermission(BoxStaffPermissions.SEE_BANK_DETAILS)
  const canSeeFinancial = useBoxStaffPermission(BoxStaffPermissions.SEE_FINANCIAL)
  const isStripeAccountComplete = box?.stripeAccountStatus === "OK"
  const {
    data: stats,
    isLoading: loading,
    isRefetching,
    updateStats,
  } = useFinanceStats(isStripeAccountComplete && canSeeFinancial, period)
  const { value: transactionId } = localStorageService.load(LocalStorageKeys.boxTransaction.transactionId, {
    value: "",
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      if (canSeeFinancial && transactionId && transactionId !== "") {
        sidebarNavigateTo({
          content: <BoxTransactionsDetailsLayout transactionId={transactionId} />,
          onHide: () => {
            localStorage.removeItem(LocalStorageKeys.boxTransaction.transactionId)
            return Promise.resolve(true)
          },
          replaceAllComponents: true,
        })
      }
    }, 2000)

    return () => {
      clearInterval(timer)
    }
  }, [transactionId, canSeeFinancial])

  const openStripeAccount = () => {
    boxNavigate(Path.Box.Admin + "?tab=banking")
  }

  const openPromoCodeSidebar = () => {
    if (offers.length > 0) navigateToPromoCodeListView()
    else toast.show({ detail: word("promoCode.toast.missingOffer"), severity: "warn" })
  }

  const cardLoading = isLoadingCurrentBox || loading

  const renderFinanceTotalMonthCard = useMemo(() => {
    return (
      <FinanceTotalMonthCard
        cardType="Primary"
        cardTitle={word("finances.page.card.ca.label")}
        value={stats ? formatCurrency(stats.totalTransactionsValue, stats.totalTransactionsCurrency, language) : "--"}
        loading={cardLoading}
        period={period}
        className="flex-grow-1 overflow-hidden rounded-33"
      />
    )
  }, [stats, cardLoading, period, language])

  const [menuTab, setMenuTab] = useState(0)
  const items: MenuItem[] = [
    {
      label: word("finances.page.menu.sales"),
      icon: <PayinIcon color={menuTab === 0 ? theme.blackAtheleWhiteOwner : theme.neutralColor300} className="mr-1" />,
    },
    {
      label: word("finances.page.menu.payouts"),
      icon: <PayoutIcon color={menuTab === 1 ? theme.blackAtheleWhiteOwner : theme.neutralColor300} className="mr-1" />,
    },
  ]

  if (!isLoadingCurrentBox && !canSeeFinancial) {
    boxNavigate(Path.Box.Planning)
  }

  if (isLoadingCurrentBox)
    return (
      <div className="flex flex-column w-full flex-grow-1">
        <LoadingSpinner />
      </div>
    )
  else if (!isStripeAccountComplete)
    return (
      <div className="w-full">
        <ScreenHeader title={word("menu.finances")} />
        <StripeStatusBanner />
      </div>
    )
  else
    return (
      <div className="flex flex-column w-full flex-grow-1">
        <ScreenHeader
          title={word("menu.finances")}
          className="flex flex-column justify-content-between align-items-start gap-2 md:flex-row md:gap-3 md:align-items-center mb-0"
        >
          {menuTab === 0 ? (
            <LastUpdateComponent isRefetchStats={isRefetching} updateStats={updateStats} />
          ) : (
            <div className="mt-6" />
          )}
        </ScreenHeader>

        <div className="flex w-full">
          <TabMenu model={items} activeIndex={menuTab} onTabChange={(e) => setMenuTab(e.index)} className="w-full" />
        </div>
        {menuTab === 0 && (
          <HUStack gap={28} className="flex-grow-1" style={{ minWidth: 1000 }}>
            {box.accountError && (
              <StripeAccountErrorItem
                orientation="horizontal"
                alertTitle={word("global.stripeError.missingDocument.title")}
                alertContent={word("global.stripeError.missingDocument.content")}
                primaryButtonLabel={word("global.stripeError.missingDocument.buttonLabel")}
                primaryButtonOnClickFn={openStripeAccount}
              />
            )}

            <HURow gap={28}>
              {renderFinanceTotalMonthCard}
              <FinanceCard
                cardType="Secondary"
                cardTitle={word("finances.page.card.promoCodes.label")}
                cardSubtitle={word("finances.page.card.promoCodes.description")}
                value={false}
                loading={cardLoading}
                buttonLabel={word("finances.page.card.promoCodes.button")}
                isButtonLoading={false}
                onButtonClick={openPromoCodeSidebar}
                className="flex-1 overflow-hidden rounded-33"
              />
              {canSeeBankDetails && (
                <FinanceCard
                  cardType="Secondary"
                  cardTitle={word("finances.page.card.myPaymentInformation")}
                  loading={cardLoading}
                  value={false}
                  icon="pi pi-wallet"
                  buttonLabel={word("finances.page.card.seeMyPaymentInformation")}
                  buttonIcon="pi pi-arrow-right"
                  onButtonClick={openStripeAccount}
                  className="flex-1 justify-content-between overflow-hidden rounded-33"
                />
              )}
            </HURow>
            <BoxTransactionsTable />
          </HUStack>
        )}
        {menuTab === 1 && <BoxPayoutLayout />}
      </div>
    )
}
